import { Button,Input } from 'components/atoms/index';
import React, { useEffect, useState } from 'react';

import logoImg from '@/assets/img/logo.png';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { academyAuthApi } from '@/services/api';
import { message } from 'antd';
import { publicAcademyApi } from '@/services/api';
import { getSlugPrefix, isReservedSegment, sanitizeSlug } from '@/utils/slug';
import { useSessionSlug } from '@/hooks/useSessionSlug';

const REJECTED_PREFILL_STORAGE_KEY = 'academy_rejected_prefill';

type RejectedPrefillPayload = {
  loginId?: string;
  academyName?: string;
  adminName?: string;
  email?: string;
  phone?: string;
  seatCount?: number;
  businessRegistrationNumber?: string;
  reason?: string;
  issuedAt?: number;
};

// 로그인 페이지
export default function AcademyLogin() {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null | undefined>(undefined);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const slugPrefix = getSlugPrefix(pathname);
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0] || '';
  const { refresh: refreshSessionSlug } = useSessionSlug();

  const extractRejectReason = (msg: string) => {
    if (!msg || typeof msg !== 'string') return '';
    const parts = msg.split('사유:');
    return parts.length > 1 ? parts[1].trim() : '';
  };

  const redirectToSignUpWithPrefill = (payload: RejectedPrefillPayload, reasonText: string) => {
    try {
      if (typeof window !== 'undefined') {
        const storeValue = {
          ...payload,
          reason: reasonText,
          issuedAt: Date.now(),
        };
        sessionStorage.setItem(REJECTED_PREFILL_STORAGE_KEY, JSON.stringify(storeValue));
      }
    } catch {
      // storage unavailable, ignore
    }
    const params = new URLSearchParams();
    params.set('from', 'login_reject');
    if (reasonText) {
      params.set('rejectMessage', reasonText);
    }
    const basePath = slugPrefix ? `${slugPrefix}/academy/sign_up` : '/academy/sign_up';
    const target = params.toString() ? `${basePath}?${params.toString()}` : basePath;
    setTimeout(() => {
      navigate(target);
    }, 800);
  };

  useEffect(() => {
    const token = localStorage.getItem('academy_token');
    if (token) {
      navigate(slugPrefix ? `${slugPrefix}/academy/home` : '/academy');
    }
  }, [navigate, slugPrefix]);

  // 학원 로고 로딩 (slug 기반)
  useEffect(() => {
    setLogoUrl(undefined);
    try {
      const pathSlug = first && !isReservedSegment(first) ? sanitizeSlug(first) : '';
      if (!pathSlug) {
        setLogoUrl(null);
        return;
      }
      publicAcademyApi
        .getByUrl(pathSlug)
        .then((res: any) => {
        const data = res?.data || res;
        const nextLogo = data?.logoUrl || null;
        setLogoUrl(nextLogo ? String(nextLogo) : null);
      })
        .catch(() => setLogoUrl(null));
    } catch {
      setLogoUrl(null);
    }
  }, [first]);

  const onLogin = async () => {
    if (!loginId || !password) {
      message.error('아이디와 비밀번호를 입력해주세요.');
      return;
    }
    setLoading(true);
    try {
      const res: any = await academyAuthApi.login({ loginId, password });
      const token = res?.data?.token || res?.token;
      const refreshToken = res?.data?.refreshToken || res?.refreshToken;
      const academyName = res?.data?.academyName || res?.academyName || '';
      const userId = res?.data?.userId || res?.userId;
      const loginIdFromResponse = res?.data?.userName || res?.userName || loginId;
      const userName = res?.data?.displayName || res?.displayName || loginIdFromResponse;
      const representativeEmail = res?.data?.email || res?.email || '';
      const role = (res?.data?.role || res?.role || '').toString().toUpperCase();
      const academyUrl = res?.data?.academyUrl || res?.academyUrl || '';
      if (token) {
        localStorage.setItem('academy_token', token);
        if (refreshToken) localStorage.setItem('academy_refresh_token', refreshToken);
        if (academyName) localStorage.setItem('academy_name', academyName);
        if (userName) localStorage.setItem('academy_user_name', userName);
        if (loginIdFromResponse) localStorage.setItem('academy_login_id', loginIdFromResponse);
        if (representativeEmail) localStorage.setItem('academy_representative_email', representativeEmail);
        if (userId) localStorage.setItem('academy_user_id', String(userId));
        if (role) {
          localStorage.setItem('academy_role', role);
        } else {
          localStorage.removeItem('academy_role');
        }
        const slugFromResponse = sanitizeSlug(academyUrl || '');
        const pathSlug = sanitizeSlug(slugPrefix ? slugPrefix.replace(/^\//, '') : '');
        let finalSlug = pathSlug || slugFromResponse;

        const sessionSlug = await refreshSessionSlug();
        const targetSlug = sessionSlug || finalSlug;

        const nextSlugPrefix = targetSlug ? `/${targetSlug}` : slugPrefix;
        message.success('로그인 성공');
        navigate(nextSlugPrefix ? `${nextSlugPrefix}/academy/home` : '/academy');
      } else {
        message.error('로그인 토큰을 받지 못했습니다.');
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const apiResponse = e?.response?.data;
      const rawMsg = apiResponse?.message || '로그인 실패';
      const payload: RejectedPrefillPayload | undefined = apiResponse?.data;
      const rejectReason = typeof rawMsg === 'string' ? extractRejectReason(rawMsg) : '';
      const rejectionKeywords = ['거절', '반려'];
      const hasRejectionKeyword =
        typeof rawMsg === 'string' && rejectionKeywords.some((keyword) => rawMsg.includes(keyword));
      const isRejected = status === 403 && hasRejectionKeyword;
      if (isRejected) {
        const rejectionAlert =
          '가입이 반려되었습니다. 반려사유 확인 후 가입 내용을 수정해주세요.'; // [QA Fix] 반려 상태 안내 문구
        message.warning(rejectionAlert);
        redirectToSignUpWithPrefill(
          {
            loginId: payload?.loginId || loginId,
            academyName: payload?.academyName,
            adminName: payload?.adminName,
            email: payload?.email,
            phone: payload?.phone,
            seatCount: payload?.seatCount,
            businessRegistrationNumber: payload?.businessRegistrationNumber,
          },
          rejectReason || (typeof rawMsg === 'string' ? rawMsg : rejectionAlert)
        );
        return;
      }
      if (status === 401) {
        message.error('아이디 또는 비밀번호가 틀렸습니다.');
        return;
      }
      if (status === 403 && typeof rawMsg === 'string') {
        message.error(rawMsg);
      } else {
        message.error(typeof rawMsg === 'string' ? rawMsg : '로그인에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <main className='flex flex-1 justify-center w-full min-h-screen bg-white lg:items-center lg:bg-transparent'>
        <section className='flex-1 px-5 py-10 h-full min-h-full lg:px-10 max-w-[600px] mx-auto'>
            <div className='flex flex-col gap-4 justify-center items-center mb-15'>
                {logoUrl === undefined ? (
                  <div className='w-[120px] h-[48px]' />
                ) : (
                <img 
                  src={logoUrl || logoImg} 
                  alt="학원 로고" 
                  className='max-h-[48px]'
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    if (target.src !== logoImg) {
                      target.src = logoImg;
                    }
                  }}
                />
                )}
                <h1 className='text-center heading1'>학원 로그인</h1>
            </div>
            <div>
                <div className='flex flex-col gap-5 items-center pb-10'>
                    <div className='w-full'>
                        <Input
                        label="아이디"
                        placeholder='아이디를 입력해주세요.'
                        value={loginId}
                        onChange={(e:any)=>setLoginId(e.target.value)}
                        onKeyDown={(e:any)=>{
                          if(e.key==='Enter'){
                            e.preventDefault();
                            onLogin();
                          }
                        }}
                        />
                        {/* 에러 메시지는 API 응답으로 토스트 처리 */}
                    </div>
                    
                    <div className='w-full'>
                    <Input
                        label="비밀번호"
                        placeholder='비밀번호를 입력해주세요.'
                        type='password'
                        value={password}
                        onChange={(e:any)=>setPassword(e.target.value)}
                        onKeyDown={(e:any)=>{
                          if(e.key==='Enter'){
                            e.preventDefault();
                            onLogin();
                          }
                        }}
                        />
                        {/* 에러 메시지는 API 응답으로 토스트 처리 */}
                    </div>
                </div>
            </div>
            <Button className='mb-3 w-full' onClick={onLogin} disabled={loading}>{loading ? '로그인 중...' : '로그인'}</Button>
            <div className='flex justify-center items-center'>
                <Button size='small' text><Link to={slugPrefix ? `${slugPrefix}/academy/sign_up` : "/academy/sign_up"}>가입 신청 하기</Link></Button>
            </div>
            <div className='flex gap-1 justify-center items-center pt-3'>
            <Button size='small' text intent='secondary' ><Link to={slugPrefix ? `${slugPrefix}/academy/find_id` : "/academy/find_id"}>아이디 찾기</Link></Button>
            <p className='text-xs text-gray-600'>|</p>
            <Button size='small' text intent='secondary' ><Link to={slugPrefix ? `${slugPrefix}/academy/find_password` : "/academy/find_password"}>비밀번호 찾기</Link></Button>
            </div>
        </section>
    </main>
  )
}